import { useEffect } from "react";

/**
 * AM-5828 — this repo has ZERO document-level drag/drop handlers, and the
 * legitimate dropzones (e.g. the Create Claim attachments picker) are small
 * targets inside long forms. A file dropped anywhere outside one of those
 * targets falls through to the browser's default handling, which navigates
 * the whole SPA away to the dropped file — silently destroying a part-filled
 * form and failing the upload, with no error of any kind.
 *
 * `dragover` must also be prevented, not just `drop`: a `drop` event only
 * fires on an element whose `dragover` handler called `preventDefault()` —
 * otherwise the browser's own drag-and-drop handling (the navigate-away
 * default) intercepts it before `drop` is ever dispatched.
 *
 * A listener registered on `document` runs in the bubble phase AFTER a
 * legitimate dropzone's own handler (which already calls
 * `preventDefault()`), so this does not interfere with drops that land on a
 * real dropzone — it only catches drops that land elsewhere and would
 * otherwise hit the browser default.
 *
 * Mount once for the whole app.
 */
export function useDropNavigationGuard(): void {
  useEffect(() => {
    const preventDefault = (event: DragEvent) => {
      event.preventDefault();
    };

    document.addEventListener("dragover", preventDefault);
    document.addEventListener("drop", preventDefault);

    return () => {
      document.removeEventListener("dragover", preventDefault);
      document.removeEventListener("drop", preventDefault);
    };
  }, []);
}
